import React from 'react';
import { Container } from 'react-bootstrap';

/* ---- "One map. Two ways to show it." ----
   One-source → two-destinations diagram: the user's map feeds both a website
   embed and a standalone public page. Replaces the old single-line embed
   section. Shared between the default and vertical landing pages. */

interface TwoWaysSectionProps {
  band?: 'light' | 'white';
  eyebrow?: string;
  title?: string;
  intro?: string;
  companySlug?: string; // shown in the "share the link" pill
  revealRef?: (el: HTMLDivElement | null) => void;
}

const TwoWaysSection: React.FC<TwoWaysSectionProps> = ({
  band = 'white',
  eyebrow = 'Share it your way',
  title = 'One map. Two ways to show it.',
  intro = 'Add a pin once — it appears everywhere your map lives. No re-uploading, no syncing.',
  companySlug = 'yourcompany',
  revealRef,
}) => {
  return (
    <section className={`landing-section${band === 'light' ? ' landing-section--light' : ''}`}>
      <Container>
        <div className="landing-ways landing-reveal" ref={revealRef}>
          <div className="landing-ways__header">
            <span className="landing-feature__eyebrow">{eyebrow}</span>
            <h2>{title}</h2>
            <p>{intro}</p>
          </div>

          <div className="landing-ways__grid">
            {/* --- Source: the user's own map --- */}
            <div className="landing-ways__source" aria-hidden="true">
              <div className="landing-ways__source-bar">
                <span className="landing-ways__source-dot" />
                <span className="landing-ways__source-label">Your map</span>
              </div>
              <div className="landing-ways__source-map">
                <span className="landing-map-dot" style={{ left: '24%', top: '26%' }} />
                <span className="landing-map-dot" style={{ left: '48%', top: '52%' }} />
                <span className="landing-map-dot" style={{ left: '68%', top: '30%' }} />
                <span className="landing-map-dot" style={{ left: '36%', top: '74%' }} />
              </div>
            </div>

            {/* --- Connectors --- */}
            <div className="landing-ways__connectors" aria-hidden="true">
              <span className="landing-ways__wire landing-ways__wire--up" />
              <span className="landing-ways__wire landing-ways__wire--down" />
            </div>

            {/* --- Destinations --- */}
            <div className="landing-ways__dests">
              {/* A — website embed */}
              <div className="landing-ways__card">
                <div className="landing-ways__mini" aria-hidden="true">
                  <div className="landing-ways__mini-bar">
                    <span className="landing-ways__mini-dot" />
                    <span className="landing-ways__mini-dot" />
                  </div>
                  <div className="landing-ways__mini-body">
                    <span className="landing-ways__mini-line" />
                    <span className="landing-ways__mini-map">
                      <span className="landing-ways__mini-pin" style={{ left: '40%', top: '38%' }} />
                    </span>
                    <span className="landing-ways__mini-line landing-ways__mini-line--wide" />
                  </div>
                </div>
                <div>
                  <div className="landing-ways__card-title">On your website</div>
                  <div className="landing-ways__card-body">
                    Paste one line into any CMS — Wix, WordPress, Squarespace, anything.
                  </div>
                  <code className="landing-ways__code">&lt;iframe src="mapofprojects.com/embed/…"&gt;</code>
                </div>
              </div>

              {/* B — standalone public page */}
              <div className="landing-ways__card">
                <div className="landing-ways__mini landing-ways__mini--page" aria-hidden="true">
                  <span className="landing-ways__mini-topbar">
                    <span />
                  </span>
                  <span className="landing-ways__mini-fill">
                    <span className="landing-ways__mini-pin" style={{ left: '30%', top: '30%' }} />
                    <span className="landing-ways__mini-pin" style={{ left: '60%', top: '55%' }} />
                  </span>
                </div>
                <div>
                  <div className="landing-ways__card-title">No website? No problem.</div>
                  <div className="landing-ways__card-body">
                    Your map is already a public page with your logo and colors. Just share the link — in emails, quotes, and tenders.
                  </div>
                  <span className="landing-ways__pill">
                    🔗 mapofprojects.com/{companySlug}
                    <span className="landing-ways__pill-copy">Copy</span>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Container>
    </section>
  );
};

export default TwoWaysSection;
